import React, { useState } from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

function TimeEntriesReviewCard(props) {
  const status = props.selectedPayPeriod?.pay_period_approvals?.[0]?.status // pending_approval_initial, pending_approval_again, approved, disputed, missed_approval
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [note, setNote] = React.useState("");

  // If there's no status, don't render anything
  if (!status) {
    return null;
  }

  // [POST] api/clock_info/post_pay_period_approval (company employee)
  // * store_id (auth)
  // * pay_period_uuid
  // * pay_period_approval_uuid [optional]
  // * status [optional]
  // * note [optional]

  const handleApprove = () => {
    props.post_pay_period_approval(
      props.selectedPayPeriod?.uuid,
      props.selectedPayPeriod?.pay_period_approvals?.[0]?.uuid,
      "approved",
      null
    )
  };

  const handleNeedChanges = () => {
    setShowNoteInput(true);
  };

  const handleSubmitDispute = () => {
    setShowNoteInput(false);
    props.post_pay_period_approval(
      props.selectedPayPeriod?.uuid,
      props.selectedPayPeriod?.pay_period_approvals?.[0]?.uuid,
      "disputed",
      note
    )
  };

  const getCardStyle = () => {
    switch (status) {
      case 'approved':
        return 'border-emerald-300 bg-emerald-50';
      case 'disputed':
        return 'border-rose-300 bg-rose-50';
      case 'missed_approval':
        return 'border-orange-300 bg-orange-50';
      case 'pending_approval_initial':
      case 'pending_approval_again':
        return 'border-amber-300 bg-amber-50';
      default:
        return 'border-gray-300 bg-gray-50';
    }
  };

  const getTextStyle = () => {
    switch (status) {
      case 'approved':
        return 'text-emerald-800';
      case 'disputed':
        return 'text-rose-800';
      case 'missed_approval':
        return 'text-orange-800';
      case 'pending_approval_initial':
      case 'pending_approval_again':
        return 'text-amber-800';
      default:
        return 'text-gray-800';
    }
  };

  const renderIcon = () => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon className="h-5 w-5 text-emerald-500 flex-shrink-0" />;
      case 'disputed':
        return <ErrorIcon className="h-5 w-5 text-rose-500 flex-shrink-0" />;
      case 'missed_approval':
        return <WarningAmberIcon className="h-5 w-5 text-orange-500 flex-shrink-0" />;
      case 'pending_approval_initial':
      case 'pending_approval_again':
        return <WarningAmberIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />;
      default:
        return null;
    }
  };

  const renderPendingApprovalContent = (type) => {
    return (
      <div className={getTextStyle()}>
        {type === "again" && props.selectedPayPeriod?.pay_period_approvals?.[0]?.note && <p className="mb-3 text-sm italic">Note from Manager: {props.selectedPayPeriod?.pay_period_approvals?.[0]?.note}</p>}
        <div className="text-sm mb-4">
          <p>• Time Entries: {props.numOfTimeEntries}</p>
          <p>• Total Hours: {props.selectedPayPeriod?.timecard_summary_data?.payable_hours?.toFixed(props.timeEntriesHoursRoundedToTenths ? 1 : 2)}</p>
        </div>
        {!showNoteInput && (
          <div className="flex gap-3">
            <button
              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
              onClick={handleApprove}
            >
              Approve Entries
            </button>
            <button
              className="px-4 py-2 border border-amber-500 text-amber-700 rounded-md hover:bg-amber-100 transition-colors"
              onClick={handleNeedChanges}
            >
              Need Changes
            </button>
          </div>
        )}
        {showNoteInput && (
          <div className="space-y-3">
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md focus:border-violet-700 focus:outline-none focus:ring-1 focus:ring-violet-700 text-slate-900"
              placeholder="Add a note (optional)"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="flex gap-3">
              <button
                className="px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700 transition-colors"
                onClick={handleSubmitDispute}
              >
                Submit Dispute
              </button>
              <button
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
                onClick={() => setShowNoteInput(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (status) {
      case 'approved':
        return (
          <div className={getTextStyle()}>
            <div className="text-sm">
              <p>• Time Entries: {props.numOfTimeEntries}</p>
              <p>• Total Hours: {props.selectedPayPeriod?.timecard_summary_data?.payable_hours?.toFixed(props.timeEntriesHoursRoundedToTenths ? 1 : 2)}</p>
            </div>
          </div>
        );
      case 'disputed':
        return (
          <div className={getTextStyle()}>
            {props.selectedPayPeriod?.pay_period_approvals?.[0]?.note && <p className="mb-3 text-sm italic">Note to Manager: {props.selectedPayPeriod?.pay_period_approvals?.[0]?.note}</p>}
            <div className="text-sm">
              <p>• Time Entries: {props.numOfTimeEntries}</p>
              <p>• Total Hours: {props.selectedPayPeriod?.timecard_summary_data?.payable_hours?.toFixed(props.timeEntriesHoursRoundedToTenths ? 1 : 2)}</p>
            </div>
          </div>
        );
      case 'missed_approval':
        return (
          <div className={getTextStyle()}>
            <div className="text-sm">
              <p>• Time Entries: {props.numOfTimeEntries}</p>
              <p>• Total Hours: {props.selectedPayPeriod?.timecard_summary_data?.payable_hours?.toFixed(props.timeEntriesHoursRoundedToTenths ? 1 : 2)}</p>
            </div>
          </div>
        );
      case 'pending_approval_initial':
        return renderPendingApprovalContent("initial");
      case 'pending_approval_again':
        return renderPendingApprovalContent("again");
      default:
        return null;
    }
  };

  return (
    <div className={`border rounded-lg p-4 w-96 ${getCardStyle()}`}>
      <div className="flex items-start space-x-3">
        {renderIcon()}
        <div className="w-full">
          <div className={`flex items-center gap-2 font-semibold mb-3 ${getTextStyle()}`}>
            <h3>
              {status === 'approved' && 'Time Entries Approved'}
              {status === 'disputed' && 'Time Entries Disputed'}
              {status === 'pending_approval_initial' && 'Review Time Entries'}
              {status === 'pending_approval_again' && 'Review Time Entries Again'}
              {status === 'missed_approval' && 'Missed Approval Period'}
            </h3>
          </div>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default TimeEntriesReviewCard;
